import React from 'react';
import { Box, Divider, Card, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useQueryClient } from '@tanstack/react-query';
import Checkbox from '../../../inputs/Checkbox';
import { isMfaEnforced } from '../../../../utils/mfa.utils';
import { useActor } from '../../../hooks/useActor';
import { useEnvQuery } from '../../../../queries/useEnvQuery';
import { Show } from '../../../Show';
import { ENVIRONMENTS } from '../../../../types/common/env.dto';
import { usePlatformLanguage } from '../../../../utils/hooks/usePlatformLanguage';
import { getIsUsPlatform } from '../../../../utils';
import { Modal } from '../../../modals/v2/helpers/v2.modal.helpers';
import { DisableMFAConfig } from '../../../modals/v2/DisableMFA';
import { MfaEnabledConfig } from '../../../modals/MfaEnabled';
import { SMSEnabledConfig } from '../../../modals/SMSEnabled';
import { useMeQuery } from '../../../hooks/useMeQuery';
import withNewThemeProvider from '../../../hocs/withNewThemeProvider';

export const MultiFactor = withNewThemeProvider(() => {
  const classes = useStyles();
  const { data: env } = useEnvQuery();
  const queryClient = useQueryClient();

  const region = usePlatformLanguage();
  const isUSRegion = getIsUsPlatform(region);

  const actor = useActor();
  React.useEffect(() => {
    // * ensures actor hook has latest MFA information upon render
    queryClient.invalidateQueries([useMeQuery.QUERY_KEY]);
  }, [queryClient]);

  const createOpenEnableModal = (modalName: any) => {
    const enableModalProperties = enableMappings[modalName];

    return Modal.deferShow(enableModalProperties.config, {
      data: { type: enableModalProperties.type },
      config: {
        title: enableModalProperties.title,
      },
    });
  };

  const openDisableModal = () => {
    Modal.show(DisableMFAConfig, { data: { email: actor.email } });
  };

  return (
    <Card>
      <Box pl={3} pb={3} pr={3}>
        <Grid item md={10}>
          <Box py={2}>
            <Typography>MULTI FACTOR AUTHENTICATION</Typography>
            <Divider />
          </Box>

          {isMfaEnforced({
            mfaType: actor.mfaType,
            account: actor.account,
          }) && (
            <Typography variant="body1" className={classes.note}>
              Note: Multi-factor authentication is required to be set up for
              your account
            </Typography>
          )}

          <Grid className={classes.wrapper}>
            <span className="modal-title">
              Select 2nd Authentication Method
            </span>

            <div className={classes.flexBox}>
              <Checkbox
                name="google"
                label="Authenticator App (Google, Microsoft, OKTA)"
                checked={actor.mfaType === MfaTypes.GOOGLE}
                disabled={[MfaTypes.SMS, MfaTypes.EMAIL].includes(
                  actor.mfaType
                )}
                onClick={
                  actor.mfaType === MfaTypes.NONE
                    ? createOpenEnableModal(MfaTypes.GOOGLE)
                    : openDisableModal
                }
              />
              <Show when={env?.envName !== ENVIRONMENTS.STAGING_ENV_ID}>
                <Checkbox
                  name="sms"
                  label="SMS"
                  checked={actor.mfaType === MfaTypes.SMS}
                  disabled={
                    !isUSRegion ||
                    !actor.phone ||
                    [MfaTypes.GOOGLE, MfaTypes.EMAIL].includes(actor.mfaType)
                  }
                  onClick={
                    actor.mfaType === MfaTypes.NONE
                      ? createOpenEnableModal(MfaTypes.SMS)
                      : openDisableModal
                  }
                />
              </Show>

              <Show when={env?.envName !== ENVIRONMENTS.STAGING_ENV_ID}>
                <Checkbox
                  name="email"
                  label="Email"
                  checked={actor.mfaType === MfaTypes.EMAIL}
                  disabled={[MfaTypes.GOOGLE, MfaTypes.SMS].includes(
                    actor.mfaType
                  )}
                  onClick={
                    actor.mfaType === MfaTypes.NONE
                      ? createOpenEnableModal(MfaTypes.EMAIL)
                      : openDisableModal
                  }
                />
              </Show>
              {!actor.phone ? (
                <Typography
                  variant="body1"
                  className={classes.note}
                  color="secondary"
                >
                  Note: Add Contact Phone in Profile to enable SMS
                  Authentication!
                </Typography>
              ) : null}
              {actor.mfaType !== MfaTypes.NONE ? (
                <Typography
                  variant="body1"
                  className={classes.note}
                  color="secondary"
                >
                  Note: Only one MFA type may be enabled at a time
                </Typography>
              ) : null}
            </div>
          </Grid>
          <Divider />
        </Grid>
      </Box>
    </Card>
  );
});

export default MultiFactor;

const MfaTypes = {
  NONE: 'NONE',
  GOOGLE: 'GOOGLE',
  SMS: 'SMS',
  EMAIL: 'EMAIL',
};

const enableMappings = {
  [MfaTypes.GOOGLE]: {
    config: MfaEnabledConfig,
    title: 'Setup Authenticator App (Google, Microsoft, OKTA)',
  },
  [MfaTypes.SMS]: {
    config: SMSEnabledConfig,
    title: 'Setup SMS Authenticaton',
    type: MfaTypes.SMS,
  },
  [MfaTypes.EMAIL]: {
    config: SMSEnabledConfig,
    title: 'Enable Email Authenticaton',
    type: MfaTypes.EMAIL,
  },
};

// @ts-expect-error TS(2339): Property 'common' does not exist on type 'DefaultT... Remove this comment to see the full error message
const useStyles = makeStyles(({ common }) => {
  return {
    wrapper: {
      margin: '1rem 5rem 0 5rem',
    },
    flexBox: {
      display: 'flex',
      flexDirection: 'column',
      padding: 10,
    },
    note: {
      fontStyle: 'italic',
      fontSize: common.fontSizes.normal,
      whiteSpace: 'nowrap',
      margin: 10,
    },
    headings: {
      marginBottom: 0,
      marginTop: 32,
      fontSize: common.fontSizes.normal,
    },
    divider: {
      marginTop: '2.5rem',
      marginBottom: '1.6666666667rem',
    },
  };
});
